import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import { Box } from "@mui/material";
import { useSnackbar } from "notistack";

import * as BotpressSessionActions from "../../state/redux/botpressSession";
import { deepCopyObject } from "../../utils/helpers.js";
import AlertDialog from "../dialogs//alertDialog";
import BotpressBulkFeedbackDialog from "../dialogs/botpressBulkFeedbackDialog.js";
import BotpressConversationDialog from "../dialogs/botpressConversationDialog.js";
import BotpressFeedbackDialog from "../dialogs/botpressUserFeedbackDialog.js";
import BotpressFeedbackTable from "../ui/botpressFeedbackTable.js";
import Widget from "../ui/widget.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressUserFeedback = props => {
  const {
    feedback,
    searchFeedbackRequest,
    deleteChatRequest,
    feedbackPagination,
    feedbackPageLoading,
    loadConversationRequest,
    setRerunConversationIdRequest,
    deleteFeedbackRequest,
    botODM,
    updateFeedbackRequest,
    widgetSize = "large",
    minHeight = "333px",
    // "test" binds the widget to the TEST bot's feedback (test harness portal);
    // anything else (default) binds it to the PROD bot's feedback (conversations
    // portal). The redux connect below swaps the backing slice/actions/bot to
    // match, so the component body stays variant-agnostic apart from labelling.
    variant,
  } = props;

  const isTestVariant = variant === "test";

  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    if (feedback != null) {
      setRows(feedback);
    } else {
      setRows([]);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  const onRequestNewPage = async pagination => {
    try {
      searchFeedbackRequest(pagination);
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  useEffect(() => {
    if (selectedConversation != null) {
      debugger;
      // Scope the load to this widget's bot (TEST feedback conversations live in
      // the TEST bot, PROD feedback conversations in the PROD bot).
      loadConversationRequest({ conversationId: selectedConversation.conversationId, botODM });
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  const onHandleDeveloperLogs = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      const theURL = `https://app.botpress.cloud/workspaces/${botODM.omniData.workspaceId}/bots/${botODM.omniData.botId}/conversations/${conversationId}/messages/${conversationRow.messageId} `;
      window.open(theURL, "_blank");

      //got to page.
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  const [deleteRows, setDeleteRows] = useState(null);

  const onDeleteFeedback = rowsIDs => {
    try {
      if (rowsIDs == null) {
        return;
      }
      setDeleteRows(rowsIDs);
    } catch (error) {
      console.error("Error deleting table rows", error);
      setRows([]);
    }
  };

  const [editRows, setEditRows] = useState(null);

  const onBulkEditFeedback = rows => {
    try {
      if (rows == null) {
        return;
      }
      setEditRows(rows);
    } catch (error) {
      console.error("Error editing table rows", error);
      setRows([]);
    }
  };

  const onRerunConversation = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      let conversationNumber = conversationRow.conversationNumber;
      let feedbackNumber = conversationRow.id;
      setRerunConversationIdRequest({
        rerunConversationId: conversationId,
        rerunConversationNumber: conversationNumber.toString(),
        rerunFeedbackNumber: feedbackNumber,
        // PROD-variant feedback conversations live in the PROD (botpressBotODM)
        // bot; TEST-variant ones live in the TEST (testBotODM) bot.
        rerunIsTestBot: isTestVariant,
      });
    } catch (error) {
      console.error("Error getting table rows", error);
    }
  };
  const onRefreshTable = () => {
    let newPagination = deepCopyObject(feedbackPagination);
    newPagination["invalidateCache"] = true;
    onRequestNewPage(newPagination);
  };
  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={isTestVariant ? "Testing Feedback" : "Production Feedback"}
        text={`${feedbackPagination.totalItems} User Comments`}
      />

      <WidgetSurveyContent>
        <BotpressFeedbackTable
          data={rows}
          isLoading={feedbackPageLoading}
          onEditEntry={setSelectedEntry}
          onRequestNewPage={onRequestNewPage}
          dataReduxPagination={feedbackPagination}
          onViewConversation={setSelectedConversation}
          totalItemCount={feedbackPagination.totalItems}
          onHandleDeveloperLogs={onHandleDeveloperLogs}
          onRerunConversation={onRerunConversation}
          onDeleteFeedback={onDeleteFeedback}
          onBulkEditFeedback={onBulkEditFeedback}
          onRefreshTable={onRefreshTable}
          refreshTooltipTitle="Refresh feedback"
        />
      </WidgetSurveyContent>

      {/* Skeleton overlay while a page loads. The table stays mounted underneath
          (it self-fetches on mount/pagination) so we cover it rather than unmount
          it — unmounting would re-trigger the fetch in a loop. */}
      {feedbackPageLoading && (
        <Box sx={{ position: "absolute", top: "56px", left: 0, right: 0, bottom: 0, zIndex: 2 }}>
          <WidgetSkeleton />
        </Box>
      )}

      {selectedEntry != null && (
        <BotpressFeedbackDialog
          open={selectedEntry != null}
          userFeedbackRecord={selectedEntry}
          botODM={botODM}
          onViewConversation={() => {
            setSelectedConversation(selectedEntry);
            setSelectedEntry(null);
          }}
          onClose={() => {
            setSelectedEntry(null);
          }}
          onAccept={theRecord => {
            updateFeedbackRequest({ feedbackRecords: [theRecord] });
            setSelectedEntry(null);
          }}
          onDelete={theRecord => {
            props.deleteFeedbackRequest({ feedbackIDs: [theRecord.id] });
            setSelectedEntry(null);
          }}
        />
      )}

      {selectedConversation != null && (
        <BotpressConversationDialog
          conversationId={selectedConversation.conversationId}
          conversationNumber={selectedConversation.conversationNumber}
          conversation={props.conversation}
          botODM={botODM}
          dialogTitle={`Conversation #${selectedConversation.conversationNumber}`}
          conversationFeedback={props.conversationFeedback}
          deleteFeedbackRequest={props.deleteFeedbackRequest}
          updateFeedbackRequest={updateFeedbackRequest}
          messageId={selectedConversation.messageId}
          userODM={props.userODM}
          open={selectedConversation != null}
          defaultValue={selectedConversation}
          onAccept={() => {
            loadConversationRequest(null);
            setSelectedConversation(null);
          }}
          onClose={() => {
            loadConversationRequest(null);
            setSelectedConversation(null);
          }}
          onDelete={() => {
            deleteChatRequest({ conversationId: selectedConversation.id, botODM });
            loadConversationRequest(null);
            setSelectedConversation(null);
            enqueueSnackbar("Conversation Deleted Successfully", {
              variant: "success",
            });
          }}
        />
      )}

      {deleteRows && (
        <AlertDialog
          open={deleteRows}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete these Comments?"}
          onAccept={() => {
            deleteFeedbackRequest({ feedbackIDs: deleteRows });
            setDeleteRows(null);
          }}
          onClose={() => {
            setDeleteRows(null);
          }}
        />
      )}

      {editRows && (
        <BotpressBulkFeedbackDialog
          open={editRows}
          dialogTitle={`Bulk Edit`}
          dialogMessage={"Are you sure you want to edit these Comments?"}
          onAccept={updates => {
            // let theUpdates = {
            //   status: status,
            //   archived: archived,
            //   response: response,
            // };
            for (let i = 0; i < editRows.length; i++) {
              editRows[i].status = updates.status;
              editRows[i].archived = updates.archived;
              editRows[i].response = editRows[i].response
                ? editRows[i].response + " - " + updates.response
                : updates.response;
            }
            updateFeedbackRequest({ feedbackRecords: editRows });
            setEditRows(null);
          }}
          onClose={() => {
            setEditRows(null);
          }}
        />
      )}
    </Widget>
  );
};

// The widget is rendered in two places:
//   - Conversations portal: PROD bot feedback ("Production Feedback").
//   - Test harness portal: TEST bot feedback ("Testing Feedback"), via variant="test".
// The `variant` prop selects which redux slice / actions / bot back the widget,
// keeping the component body identical for both modes.
const mapStateToProps = (state, ownProps) => {
  const shared = {
    conversation: state.botpressSession.conversation,
    conversationFeedback: state.botpressSession.conversationFeedback,
    userODM: state.userSession.userODM,
  };

  if (ownProps.variant === "test") {
    return {
      ...shared,
      feedback: state.botpressSession.testFeedback,
      feedbackPagination: state.botpressSession.testFeedbackPagination,
      feedbackPageLoading: state.botpressSession.testFeedbackPageLoading,
      botODM: state.botpressSession.testBotODM,
    };
  }

  return {
    ...shared,
    feedback: state.botpressSession.feedback,
    feedbacksStartDate: state.botpressSession.feedbacksStartDate,
    feedbacksEndDate: state.botpressSession.feedbacksEndDate,
    feedbackPagination: state.botpressSession.feedbackPagination,
    feedbackPageLoading: state.botpressSession.feedbackPageLoading,
    botODM: state.botpressSession.botpressBotODM,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const shared = {
    deleteChatRequest: payload => dispatch(BotpressSessionActions.deleteChatRequest(payload)),
    loadConversationRequest: payload =>
      dispatch(BotpressSessionActions.loadConversationRequest(payload)),
    setRerunConversationIdRequest: payload =>
      dispatch(BotpressSessionActions.setRerunConversationIdRequest(payload)),
  };

  if (ownProps.variant === "test") {
    return {
      ...shared,
      searchFeedbackRequest: payload =>
        dispatch(BotpressSessionActions.searchTestFeedbackRequest(payload)),
      deleteFeedbackRequest: payload =>
        dispatch(BotpressSessionActions.deleteTestFeedbackRequest(payload)),
      updateFeedbackRequest: payload =>
        dispatch(BotpressSessionActions.updateTestFeedbackRequest(payload)),
    };
  }

  return {
    ...shared,
    searchFeedbackRequest: payload =>
      dispatch(BotpressSessionActions.searchFeedbackRequest(payload)),
    deleteFeedbackRequest: payload =>
      dispatch(BotpressSessionActions.deleteFeedbackRequest(payload)),
    updateFeedbackRequest: payload =>
      dispatch(BotpressSessionActions.updateFeedbackRequest(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressUserFeedback);
